import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Copy, Check } from "lucide-react";
import { getAuditEntry, type AuditLogEntry } from "../api";
import PageHeader from "../components/PageHeader";
import Timestamp from "../components/Timestamp";
import Tooltip from "../components/Tooltip";
import { usePageTitle } from "../hooks/usePageTitle";
import { getAuditActionConfig } from "../config/audit";
import { UI_TIMING } from "../config/constants";

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), UI_TIMING.copyFeedbackMs);
  };

  return (
    <Tooltip content={copied ? "Copied!" : "Copy to clipboard"} position="top">
      <button
        onClick={handleCopy}
        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-dark-border transition-colors flex-shrink-0"
        aria-label="Copy to clipboard"
      >
        {copied ? (
          <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
        ) : (
          <Copy className="w-4 h-4 text-gray-400 dark:text-dark-text-muted" />
        )}
      </button>
    </Tooltip>
  );
}

function ActionBadge({ action }: { action: string }) {
  const { label, color } = getAuditActionConfig(action);
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}
    >
      {label}
    </span>
  );
}

function DetailRow({
  label,
  children,
  copyValue,
  fullWidth = false,
}: {
  label: string;
  children: React.ReactNode;
  copyValue?: string;
  fullWidth?: boolean;
}) {
  return (
    <div className={fullWidth ? "col-span-2" : ""}>
      <dt className="text-sm font-medium text-gray-500 dark:text-dark-text-secondary mb-1">
        {label}
      </dt>
      <dd className="text-sm text-gray-900 dark:text-dark-text flex items-start gap-2">
        <span className="flex-1 min-w-0">{children}</span>
        {copyValue && <CopyButton value={copyValue} />}
      </dd>
    </div>
  );
}

export default function AuditDetailPage() {
  usePageTitle("Audit Entry");
  const { id } = useParams<{ id: string }>();
  const [entry, setEntry] = useState<AuditLogEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadEntry(id);
    }
  }, [id]);

  async function loadEntry(entryId: string) {
    try {
      setLoading(true);
      const data = await getAuditEntry(entryId);
      setEntry(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load audit entry");
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div className="space-y-6">
        <PageHeader>
          <PageHeader.Breadcrumb to="/audit" label="Audit Log" />
          <PageHeader.Title>Audit Entry</PageHeader.Title>
        </PageHeader>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error || "Audit entry not found"}
        </div>
      </div>
    );
  }

  const detailsJson = entry.details ? JSON.stringify(entry.details, null, 2) : null;

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeader.Breadcrumb to="/audit" label="Audit Log" />
        <PageHeader.Title>{getAuditActionConfig(entry.action).label}</PageHeader.Title>
        <PageHeader.Subtitle>
          <Timestamp date={entry.createdAt} />
        </PageHeader.Subtitle>
      </PageHeader>

      <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text">Entry Details</h2>
        </div>

        <dl className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <DetailRow label="ID" copyValue={entry.id}>
            <code className="font-mono text-xs bg-gray-100 dark:bg-dark-bg-tertiary px-2 py-1 rounded">
              {entry.id}
            </code>
          </DetailRow>

          <DetailRow label="When">
            <Timestamp date={entry.createdAt} />
          </DetailRow>

          <DetailRow label="Action">
            <ActionBadge action={entry.action} />
            <span className="ml-2 text-gray-500 dark:text-dark-text-secondary font-mono text-xs">
              ({entry.action})
            </span>
          </DetailRow>

          <DetailRow label="User" copyValue={entry.userEmail}>
            <span>{entry.userEmail}</span>
            <span className="ml-2 text-gray-500 dark:text-dark-text-secondary text-xs">
              (ID: {entry.userId})
            </span>
          </DetailRow>

          <DetailRow label="Resource Type">
            <span className="capitalize">{entry.resourceType}</span>
          </DetailRow>

          <DetailRow label="Resource ID" copyValue={entry.resourceId}>
            <code className="font-mono text-xs bg-gray-100 dark:bg-dark-bg-tertiary px-2 py-1 rounded">
              {entry.resourceId}
            </code>
          </DetailRow>

          {entry.resourceName && (
            <DetailRow label="Resource Name" copyValue={entry.resourceName}>
              {entry.resourceName}
            </DetailRow>
          )}

          <DetailRow label="IP Address" copyValue={entry.ipAddress || undefined}>
            {entry.ipAddress ? (
              <code className="font-mono text-xs bg-gray-100 dark:bg-dark-bg-tertiary px-2 py-1 rounded">
                {entry.ipAddress}
              </code>
            ) : (
              <span className="text-gray-400 dark:text-dark-text-muted">—</span>
            )}
          </DetailRow>

          {entry.userAgent && (
            <DetailRow label="User Agent" copyValue={entry.userAgent} fullWidth>
              <span className="text-xs break-all">{entry.userAgent}</span>
            </DetailRow>
          )}
        </dl>
      </div>

      {detailsJson && (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text">Details</h2>
            <CopyButton value={detailsJson} />
          </div>
          <div className="p-6">
            <pre className="text-sm font-mono bg-gray-50 dark:bg-dark-bg-tertiary p-4 rounded-lg overflow-x-auto text-gray-800 dark:text-dark-text">
              {detailsJson}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
